"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const client_library_1 = require("@seneca/client-library");
const AssignmentBadRequestError_1 = __importDefault(require("./errors/AssignmentBadRequestError"));
const AssignmentBodyNotValidError_1 = __importDefault(require("./errors/AssignmentBodyNotValidError"));
const AssignmentDoesNotExistError_1 = __importDefault(require("./errors/AssignmentDoesNotExistError"));
const BadRequestParametersError_1 = __importDefault(require("./errors/BadRequestParametersError"));
const EntityNotFoundError_1 = __importDefault(require("./errors/EntityNotFoundError"));
const MarkExamQuestionNotFoundError_1 = __importDefault(require("./errors/MarkExamQuestionNotFoundError"));
const UserNotPermittedError_1 = __importDefault(require("./errors/UserNotPermittedError"));
class AssignmentsServiceClient extends client_library_1.Client {
    constructor(fetch, options) {
        super(fetch, options);
    }
    async createAssignment(assignment) {
        const { classId, ...restOfAssignment } = assignment;
        return await this._makeAssignmentRequest({
            method: 'POST',
            classId,
            body: restOfAssignment
        });
    }
    async fetchAssignment(classId, assignmentId) {
        return await this._makeAssignmentRequest({
            method: 'GET',
            classId,
            assignmentId
        });
    }
    async fetchTeacherAssignments(classId, date, limit = 100, archived) {
        return this._pageMethod({
            method: this.fetchPageOfTeacherAssignments.bind(this),
            options: {
                limit,
                date,
                classId,
                archived
            }
        });
    }
    async fetchPageOfTeacherAssignments(options) {
        const { classId, lastKey, archived, date, limit } = options;
        const url = `${this.url}/api/classes/${classId}/assignments?${this.generateQueryParamString({
            ...(limit && { limit }),
            ...(typeof archived === 'boolean' && { archived }),
            ...(date && { date }),
            ...(lastKey && { lastKey })
        })}`;
        const expectedResponses = [200, 403];
        const response = await this.requestMaker.makeRequest(url, { method: 'GET' }, expectedResponses);
        if (response.status === 403) {
            throw new UserNotPermittedError_1.default();
        }
        return response.json;
    }
    async updateAssignment(assignment) {
        const { classId, id, ...restOfAssignment } = assignment;
        return await this._makeAssignmentRequest({
            method: 'PATCH',
            classId,
            assignmentId: id,
            body: restOfAssignment
        });
    }
    async deleteAssignment(classId, assignmentId) {
        return await this._makeAssignmentRequest({
            method: 'DELETE',
            classId,
            assignmentId
        });
    }
    async fetchStudentAssignments(classId, date, limit = 100, archived) {
        return this._pageMethod({
            method: this.fetchPageOfStudentAssignments.bind(this),
            options: {
                limit,
                date,
                classId,
                archived
            }
        });
    }
    async fetchPageOfStudentAssignments(options) {
        const { classId, lastKey, archived, date, limit } = options;
        const url = `${this.url}/api/students/me/assignments?${this.generateQueryParamString({
            ...(limit && { limit }),
            ...(date && { date }),
            ...(classId && { classId }),
            ...(typeof archived === 'boolean' && { archived }),
            ...(lastKey && { lastKey })
        })}`;
        const expectedResponses = [200, 403];
        const response = await this.requestMaker.makeRequest(url, { method: 'GET' }, expectedResponses);
        if (response.status === 403) {
            throw new UserNotPermittedError_1.default();
        }
        return response.json;
    }
    async fetchChildAssignment(userId, assignmentId) {
        const url = `${this.url}/api/parent/child/${userId}/assignment/${assignmentId}`;
        const expectedResponses = [200, 400, 403, 404];
        const response = await this.requestMaker.makeRequest(url, { method: 'GET' }, expectedResponses);
        if (response.status === 404) {
            const body = response.json;
            throw new EntityNotFoundError_1.default(body.reason);
        }
        if (response.status === 403) {
            throw new UserNotPermittedError_1.default();
        }
        if (response.status === 400) {
            throw new BadRequestParametersError_1.default();
        }
        return response.json;
    }
    async fetchChildAssignments(userId, date, limit = 100, archived) {
        return this._pageMethod({
            method: this.fetchPageOfChildAssignments.bind(this),
            options: {
                userId,
                limit,
                date,
                archived
            }
        });
    }
    async fetchPageOfChildAssignments(options) {
        const { userId, lastKey, archived, date, limit } = options;
        const url = `${this.url}/api/parent/child/${userId}/assignments?${this.generateQueryParamString({
            ...(limit && { limit }),
            ...(date && { date }),
            ...(typeof archived === 'boolean' && { archived }),
            ...(lastKey && { lastKey })
        })}`;
        const expectedResponses = [200, 403];
        const response = await this.requestMaker.makeRequest(url, { method: 'GET' }, expectedResponses);
        if (response.status === 403) {
            throw new UserNotPermittedError_1.default();
        }
        return response.json;
    }
    async fetchExamQuestionAnswers(classId, assignmentId) {
        const url = `${this.url}/api/classes/${classId}/assignments/${assignmentId}/exam-question-answers`;
        const expectedResponses = [200, 201, 403, 404];
        const response = await this.requestMaker.makeRequest(url, {
            method: 'GET'
        }, expectedResponses);
        if (response.status === 404) {
            throw new AssignmentDoesNotExistError_1.default(assignmentId);
        }
        if (response.status === 403) {
            throw new UserNotPermittedError_1.default();
        }
        return response.json;
    }
    async markAssignmentExamQuestion({ assignmentId, classId, questionId, assigneeUserId, teacherMark, embeddedAnswers, flagged }) {
        const url = `${this.url}/api/classes/${classId}/assignments/${assignmentId}/mark-exam-question`;
        const expectedResponses = [200, 201, 400, 403, 404];
        const body = {
            assigneeUserId,
            questionId,
            teacherMark,
            embeddedAnswers,
            flagged
        };
        const response = await this.requestMaker.makeRequest(url, {
            method: 'POST',
            body: JSON.stringify(body)
        }, expectedResponses);
        if (response.status === 404) {
            const body = response.json;
            throw new MarkExamQuestionNotFoundError_1.default(body.reason);
        }
        if (response.status === 403) {
            throw new UserNotPermittedError_1.default();
        }
        return response.json;
    }
    async _makeAssignmentRequest({ method, classId, assignmentId, body, queryParams }) {
        let url = `${this.url}/api/classes/${classId}/assignments`;
        if (assignmentId) {
            url += `/${assignmentId}`;
        }
        if (queryParams) {
            url += `?${this.generateQueryParamString(queryParams)}`;
        }
        const expectedResponses = [200, 201, 204, 400, 403, 404, 422];
        const response = await this.requestMaker.makeRequest(url, {
            method,
            body: body ? JSON.stringify(body) : undefined
        }, expectedResponses);
        if (response.status === 400) {
            const body = response.json;
            throw new AssignmentBadRequestError_1.default(body.reason);
        }
        if (response.status === 404) {
            throw new AssignmentDoesNotExistError_1.default(assignmentId);
        }
        if (response.status === 422) {
            const body = response.json;
            throw new AssignmentBodyNotValidError_1.default(body.pointer, body.reason);
        }
        if (response.status === 403) {
            throw new UserNotPermittedError_1.default();
        }
        return response.json;
    }
    generateQueryParamString(queryParams) {
        if (queryParams && Object.keys(queryParams).length > 0) {
            return Object.keys(queryParams)
                .map(param => `${param}=${encodeURIComponent(queryParams[param])}`)
                .join('&');
        }
        return '';
    }
}
exports.default = AssignmentsServiceClient;
//# sourceMappingURL=AssignmentsServiceClient.js.map